import React, { FC, useCallback, useMemo, useState } from "react";
import { LayoutChangeEvent, Platform, ScrollView } from "react-native";
import { ALIGN, Box, Layout as AuroraLayout, View, Spinner, useDevice, InfoBox, INFOBOX_TYPE } from "@lookiero/aurora";
import { useI18nMessage } from "@lookiero/i18n-react";
import { Button, Layout, Text } from "@lookiero/sty-psp-ui";
import { Tradename } from "@lookiero/sty-sp-tradename";
import { Order } from "../../../../../domain/entities/order/Order";
import { Product } from "../../../../../domain/entities/product/Product";
import { ProductList } from "../../../components/productList/ProductList";
import { Shape } from "../../../components/shape/Shape";
import { StackedBanner } from "../../../components/stackedBanner/StackedBanner";
import { useStaticInfo } from "../../../hooks/useStaticInfo";
import { DOMAIN, I18nMessages } from "../../../i18n/i18n";
import { BasketFooter } from "../footer/basketFooter/BasketFooter";
import { BasketHeader } from "../header/basketHeader/BasketHeader";
import { style as basketStyle } from "./Basket.styles";
import { Resume } from "./resume/Resume";
import { TopSellingProducts } from "./topSellingProducts/TopSellingProducts";

interface BasketProps {
  readonly layout: Layout;
  readonly isLoading: boolean;
  readonly order: Order;
  readonly benefitsImageUrl: string;
  readonly onBack: () => void;
  readonly onRemoveProduct: (product: Product) => void;
  readonly onAddToWishlist: (product: Product) => void;
  readonly onRemoveFromWishlist: (product: Product) => void;
  readonly onPressImage: (product: Product) => void;
  readonly onPressWishlist: () => void;
  readonly goToCheckout: () => void;
}

const Basket: FC<BasketProps> = ({
  layout: Layout,
  isLoading,
  order,
  benefitsImageUrl,
  onBack,
  onRemoveProduct,
  onAddToWishlist,
  onRemoveFromWishlist,
  onPressImage,
  onPressWishlist,
  goToCheckout,
}) => {
  const { screen } = useDevice();
  const { tradename } = useStaticInfo();

  const style = useMemo(() => basketStyle(), []);

  const basketHeaderTitleText = useI18nMessage({ domain: DOMAIN, id: I18nMessages.BASKET_HEADER_TITLE });
  const basketLimitedStockInfoText = useI18nMessage({
    domain: DOMAIN,
    id: I18nMessages.BASKET_LIMITED_STOCK_INFO,
  });
  const itemNotAvailableText = useI18nMessage({
    domain: DOMAIN,
    id: I18nMessages.ITEM_NOT_AVAILABLE,
  });
  const basketEmptyStateTitleText = useI18nMessage({
    domain: DOMAIN,
    id: I18nMessages.BASKET_EMPTY_STATE_TITLE,
  });
  const basketEmptyStateText = useI18nMessage({
    domain: DOMAIN,
    id: I18nMessages.BASKET_EMPTY_STATE_TEXT,
  });
  const emptyStateCtaText = useI18nMessage({
    domain: DOMAIN,
    id: I18nMessages.EMPTY_STATE_CTA,
  });
  const benefitsCylTitleText = useI18nMessage({
    domain: DOMAIN,
    id: I18nMessages.BENEFITS_CYL_TITLE,
  });
  const benefitsCylSubtitleText = useI18nMessage({
    domain: DOMAIN,
    id: I18nMessages.BENEFITS_CYL_SUBTITLE,
  });

  const [footerHeight, setFooterHeight] = useState(0);
  const handleOnLayoutFooter = useCallback(
    (event: LayoutChangeEvent) => Platform.OS !== "web" && setFooterHeight(event.nativeEvent.layout.height),
    [],
  );

  const { items: orderProducts } = order;
  const [availableProducts, unavailableProducts] = useMemo(() => {
    if (isLoading) {
      return [[], []];
    }

    return orderProducts.reduce(
      (acc, currentProduct) => {
        const [currentAvailable, currentUnavailable] = acc;

        if (currentProduct.available) {
          currentAvailable.push(currentProduct);
        } else {
          currentUnavailable.push(currentProduct);
        }

        return [currentAvailable, currentUnavailable] as [Product[], Product[]];
      },
      [[], []] as [Product[], Product[]],
    );
  }, [isLoading, orderProducts]);

  const totalPriceAmount = useMemo(() => {
    if (isLoading) {
      return 0;
    }

    return availableProducts.reduce((total, currentProduct) => {
      const hasDiscount = currentProduct.isProductWithDiscount;
      const amountPrice = hasDiscount ? currentProduct.price.discountedPrice?.amount : currentProduct.price.amount;
      return total + (amountPrice || 0);
    }, 0);
  }, [isLoading, availableProducts]);

  return (
    <Layout
      scrollEnabled={false}
      header={
        <BasketHeader
          title={`${basketHeaderTitleText} (${orderProducts?.length})`}
          onBack={onBack}
          onPressWishlist={onPressWishlist}
        />
      }
      style={{
        header: style.header,
        scrollView: [style.scrollView, screen.L ? style.desktopBackground : style.mobileBackground],
      }}
    >
      {isLoading ? (
        <View style={style.spinnerWrapper}>
          <Spinner />
        </View>
      ) : (
        <>
          <ScrollView contentContainerStyle={{ paddingBottom: footerHeight }} showsVerticalScrollIndicator={false}>
            {orderProducts.length > 0 ? (
              <>
                <View style={screen.L ? undefined : style.infoboxMobileSpacing}>
                  <InfoBox
                    alignItems={ALIGN.CENTER}
                    contentAlign={screen.L ? ALIGN.CENTER : undefined}
                    icon="info"
                    text={basketLimitedStockInfoText}
                    type={screen.L ? INFOBOX_TYPE.SQUARED : INFOBOX_TYPE.ROUNDED}
                  />
                </View>

                <AuroraLayout fullWidth={!screen.L} style={screen.L && style.desktopLayoutSpacing}>
                  <Box size={{ L: "2/3" }} style={screen.L ? style.desktopListSpacing : undefined}>
                    <ProductList
                      products={availableProducts}
                      rounded={screen.L}
                      showLastUnitsLabel
                      showLastUnitsStock
                      onAddToWishlist={onAddToWishlist}
                      onDelete={onRemoveProduct}
                      onDeleteFromWishlist={onRemoveFromWishlist}
                      onPressImage={onPressImage}
                    />

                    {!screen.L ? (
                      <Resume
                        goToCheckout={goToCheckout}
                        totalPriceAmount={totalPriceAmount}
                        dark
                        hideCta
                        hideFreeShipping
                      />
                    ) : null}
                    <ProductList
                      products={unavailableProducts}
                      rounded={screen.L}
                      style={screen.L ? style.productListSeparator : undefined}
                      title={`${itemNotAvailableText} (${unavailableProducts.length})`}
                      showNotAvailableLabel
                      onAddToWishlist={onAddToWishlist}
                      onDelete={onRemoveProduct}
                      onDeleteFromWishlist={onRemoveFromWishlist}
                      onPressImage={onPressImage}
                    />
                  </Box>

                  {screen.L ? (
                    <Box size={{ L: "1/3" }} style={style.desktopResume}>
                      <Resume goToCheckout={goToCheckout} totalPriceAmount={totalPriceAmount} />
                      <View style={style.benefitsSeparator} testID="benefits">
                        <StackedBanner
                          imageUrl={benefitsImageUrl}
                          subtitle={benefitsCylSubtitleText}
                          title={benefitsCylTitleText}
                        />
                      </View>
                    </Box>
                  ) : null}
                </AuroraLayout>

                {!screen.L ? (
                  <View style={style.separator} testID="benefits">
                    <StackedBanner
                      imageUrl={benefitsImageUrl}
                      subtitle={benefitsCylSubtitleText}
                      title={benefitsCylTitleText}
                    />
                  </View>
                ) : null}
              </>
            ) : (
              <View style={style.emptyStateContainer}>
                {tradename === Tradename.LOOKIERO && <Shape type="doubleHalf" />}
                <Box
                  style={[
                    style.emptyBasketMessage,
                    screen.L ? style.emptyBasketMessageLarge : style.emptyBasketMessageSmall,
                  ]}
                >
                  <Text level={3} variant="heading">
                    {basketEmptyStateTitleText}
                  </Text>
                  <Text level={3} style={style.emptyBasketMessageBody}>
                    {basketEmptyStateText}
                  </Text>
                  <Button
                    style={{
                      button: [style.emptyBasketMessageButton, screen.L ? undefined : { width: "100%" }],
                    }}
                    onPress={onBack}
                  >
                    {emptyStateCtaText}
                  </Button>
                </Box>
                {tradename === Tradename.LOOKIERO && <Shape type="rectangle" />}
              </View>
            )}
            <View style={style.topSellingWrapper}>
              <TopSellingProducts />
            </View>
          </ScrollView>

          {!screen.L && orderProducts.length > 0 && (
            <BasketFooter onLayout={handleOnLayoutFooter} onPress={goToCheckout} />
          )}
        </>
      )}
    </Layout>
  );
};

export { Basket };
